import { defineSlotRecipe } from "@chakra-ui/react"

/**
 * Styles for Placeholder: an empty slot shaped like the content that will
 * fill it: a soft fill with no outline, using the alpha palettes so it reads
 * as "not content yet" on both light surfaces and images.
 */
export const placeholderRecipe = defineSlotRecipe({
  className: "placeholder",
  slots: ["root", "media", "body", "label", "hint"],
  base: {
    // Root is the control: the whole slot is one button or link.
    root: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 3,
      p: 4,
      w: "full",
      appearance: "none",
      border: "none",
      font: "inherit",
      textAlign: "start",
      textDecoration: "none",
      cursor: "pointer",
      bg: "var(--placeholder-bg)",
      borderRadius: "card",
      color: "var(--placeholder-text)",
      transition: "background 0.2s, color 0.2s",
      _hover: {
        bg: "var(--placeholder-hover-bg)",
        color: "var(--placeholder-strong)",
        textDecoration: "none"
      },
      _focusVisible: {
        bg: "var(--placeholder-hover-bg)",
        color: "var(--placeholder-strong)"
      }
    },
    media: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bg: "var(--placeholder-hover-bg)",
      color: "var(--placeholder-text)"
    },
    // Label over hint as one block of text, like a title and its subtitle.
    body: {
      display: "flex",
      flexDirection: "column",
      minW: 0
    },
    label: {
      display: "inline-flex",
      alignItems: "center",
      gap: 2,
      // Standard body text, so a label matches the copy that replaces it.
      textStyle: "md",
      fontWeight: "semibold",
      color: "inherit"
    },
    // Secondary text under the label, in the muted color the page uses for
    // subtitles; it never darkens, so it does not read as content. Collapsed
    // until the slot is hovered or focused, then it opens and fades in, so an
    // idle slot holds only its label. Always open where there is no hover
    // (touch screens).
    hint: {
      display: "grid",
      gridTemplateRows: "0fr",
      opacity: 0,
      fontSize: "sm",
      lineHeight: "short",
      fontWeight: "normal",
      color: "var(--placeholder-muted)",
      transition: "grid-template-rows 0.2s ease, opacity 0.2s ease",
      "& > span": { overflow: "hidden", minH: 0 },
      "[data-placeholder]:is(:hover, :focus-visible) &": {
        gridTemplateRows: "1fr",
        opacity: 1
      },
      // The theme's `mediaHoverNone` condition, spelled out because recipes
      // are typed without the custom conditions.
      "@media (hover: none)": { gridTemplateRows: "1fr", opacity: 1 }
    }
  },
  variants: {
    tone: {
      light: {
        root: {
          "--placeholder-bg": "colors.blackAlpha.50",
          "--placeholder-hover-bg": "colors.blackAlpha.100",
          "--placeholder-text": "colors.blackAlpha.600",
          "--placeholder-muted": "colors.gray.500",
          "--placeholder-strong": "colors.blackAlpha.900"
        }
      },
      // The same frosted fill as the `blurredLightGray` button.
      onImage: {
        root: {
          "--placeholder-bg": "colors.whiteAlpha.300",
          "--placeholder-hover-bg": "colors.whiteAlpha.400",
          "--placeholder-text": "colors.whiteAlpha.800",
          "--placeholder-muted": "colors.whiteAlpha.700",
          "--placeholder-strong": "colors.white",
          backdropFilter: "blur(10px)"
        }
      }
    },
    // `pill`, `icon`, and `text` are inline slots for a missing button, item,
    // or line of text.
    shape: {
      box: {},
      pill: {
        root: {
          display: "inline-flex",
          justifyContent: "center",
          w: "auto",
          px: 5,
          py: 0,
          minH: 10,
          borderRadius: "button"
        }
      },
      // A round icon-only slot, e.g. a plus where another item would go.
      // Give Root an `aria-label`; there is no Label part to name it.
      icon: {
        root: {
          display: "inline-flex",
          justifyContent: "center",
          flexShrink: 0,
          boxSize: 8,
          p: 0,
          borderRadius: "full"
        }
      },
      text: {
        root: {
          display: "inline-flex",
          w: "auto",
          p: 0,
          bg: "transparent",
          borderRadius: 0,
          backdropFilter: "none",
          _hover: { bg: "transparent", textDecoration: "underline" },
          _focusVisible: { bg: "transparent", textDecoration: "underline" }
        }
      }
    },
    // `notice` explains what has to happen first instead of inviting an
    // action, so its label takes the hint's quieter text style.
    variant: {
      action: {},
      notice: {
        label: {
          fontSize: "sm",
          lineHeight: "short",
          fontWeight: "normal",
          color: "var(--placeholder-muted)"
        }
      }
    },
    align: {
      start: {},
      center: {
        root: {
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center"
        },
        body: { alignItems: "center" }
      }
    }
  },
  defaultVariants: {
    tone: "light",
    shape: "box",
    variant: "action",
    align: "start"
  }
})
